import React, { type FC, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { fetchTranslation, todayIsWithinDateRange } from "senf-shared";
import i18n from "senf-shared/utils/i18n";
import { useIsMobile } from "../../../hooks/customDeviceDetect";
import theme from "../../../styles/theme";
import Button from "../../core/button/Button";
import Icon from "../../core/icon/Icon";
import IdeaCardHeader from "../headers/IdeaCardHeader";
import type { IdeaCardProps } from "./IdeaCard.types";

const IdeaCard: FC<IdeaCardProps> = ({
  data,
  handle,
  user,
  project,
  ideaMap,
  ...props
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [loadingLike, setLoadingLike] = useState(false);
  const pause = useRef(false);
  const throttle = 200;
  const { title, body, likeCount, commentCount, ideaId, translations, lang } =
    data;

  const liked = user?.likes?.some((like) => like.ideaId === ideaId);
  const commented = user?.comments?.some(
    (comment) => comment.ideaId === ideaId,
  );

  const withinDateRange = todayIsWithinDateRange(
    ideaMap?.startDate,
    ideaMap?.endDate,
  );
  const inOriginalLanguage =
    project?.settings?.originalLanguage === i18n.resolvedLanguage;
  const [translatedTitle, setTranslatedTitle] = useState(null);

  useEffect(() => {
    if (
      project?.settings?.originalLanguage &&
      !inOriginalLanguage &&
      lang !== i18n.resolvedLanguage
    ) {
      fetchTranslation(project, data)
        .then((translated) => setTranslatedTitle(translated))
        .catch((error) => {
          console.error("Error fetching translation:", error);
          setTranslatedTitle(title);
        });
    } else {
      setTranslatedTitle(null);
    }
  }, [project, title, lang, i18n.resolvedLanguage]);

  const handleClick = (event) => {
    if (!pause.current) {
      handle.openCard?.(event, "ideaCard", ideaId);
      pause.current = true;
      setTimeout(() => (pause.current = false), throttle);
    }
  };

  const handleLike = (event) => {
    event.stopPropagation(); // Prevent triggering the card click
    setLoadingLike(true);
    handle.buttonLike(event, ideaId).finally(() => setLoadingLike(false));
  };

  const ariaLabel_ideaCard = `${`${`${t(
    "global:post",
  )}: ${title}`} – ${likeCount} – ${t("global:likes")} – ${commentCount} ${t(
    "global:comments",
  )}`} – Drücke Enter um Details anzusehen`;

  return (
    data && (
      <article
        className="animate-opacityTranslateY custom-light-shadow focusable hover:bg-surface-subdued relative h-auto cursor-pointer overflow-hidden whitespace-pre-wrap break-words rounded-2xl border p-3 pl-4 text-left outline-none transition duration-300 hover:scale-[1.03]"
        role="article"
        tabIndex={0}
        onClick={handleClick}
        aria-label={ariaLabel_ideaCard}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleClick(e);
          }
        }}
      >
        <div className="mb-2 flex items-center justify-between">
          <IdeaCardHeader
            data={data}
            maxWidth={
              isMobile
                ? `calc(100% - ${
                    likeCount.toString().length * 8 +
                    commentCount.toString().length * 8
                  }px)`
                : `220px`
            }
          />
          <div className="flex items-center">
            {ideaMap?.interactionOptions?.allowLikes && <Button
              aria-label={liked ? "Unlike idea" : "Like idea"}
              variant="shapeless"
              className="gap-1 whitespace-nowrap text-sm"
              style={{ minWidth: "50px" }}
              leadingIcon={liked ? "heart-fill" : "heart"}
              leadingIconProps={{
                color: liked ? theme.colors.palette.alert : "current",
              }}
              // textSubdued={!liked}
              text={`${likeCount}`}
              onClick={handleLike}
              disabled={!withinDateRange}
              disabledNotice={
                !withinDateRange && t("global:interactionNotPossibleAnymore")
              }
              // style={
              //   loadingLike
              //     ? {
              //         pointerEvents: "none",
              //         cursor: "default",
              //         opacity: 0.6,
              //       }
              //     : {}
              // }
            />}
            {ideaMap?.interactionOptions?.allowComments !== false && <Button
              aria-label="Comment on idea"
              variant="shapeless"
              className="gap-1 whitespace-nowrap text-sm"
              style={{ minWidth: "50px" }}
              leadingIcon={commented ? "comment-fill" : "comment"}
              // textSubdued={!commented}
              disabled={!withinDateRange}
              disabledNotice={
                !withinDateRange && t("global:interactionNotPossibleAnymore")
              }
              text={commentCount}
              onClick={(event) => handle.buttonComment(event, ideaId)}
            />}
          </div>
        </div>

        <div className="text-start">
          <span className="heading-xl font-[700]">
            {translatedTitle ||
              translations?.[i18n.resolvedLanguage]?.title ||
              title}
          </span>
        </div>
        {(!project?.settings?.originalLanguage || inOriginalLanguage) && (
          <div className="mt-1.5 ">
            <span className="body-md text-text-default line-clamp-3 text-start">
              {translations?.[project?.settings?.originalLanguage]?.body ||
                body}
            </span>
          </div>
        )}
      </article>
    )
  );
};

export default IdeaCard;
